import { useEffect, useRef, useState } from "react";
import type { Theme } from "./theme";

const STAGES = ["Define", "Find", "Assess", "Compare", "Decide"] as const;

export function HeroDiagram({ theme }: { theme: Theme }) {
  const [active, setActive] = useState(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (!pausedRef.current) {
        setActive((a) => (a + 1) % STAGES.length);
      }
    }, 2200);
    return () => window.clearInterval(t);
  }, []);

  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius * 1.5,
        background: theme.surface,
        padding: 32,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: theme.fgMute,
          }}
        >
          Live · Role blueprint → Decision
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <span
            style={{ width: 8, height: 8, borderRadius: 4, background: theme.accent, opacity: 0.9 }}
          />
          <span style={{ width: 8, height: 8, borderRadius: 4, background: theme.border }} />
          <span style={{ width: 8, height: 8, borderRadius: 4, background: theme.border }} />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 0,
          position: "relative",
        }}
      >
        {STAGES.map((s, i) => (
          <button
            key={s}
            type="button"
            data-stage={s}
            aria-label={`Highlight stage ${i + 1}: ${s}`}
            onFocus={() => {
              pausedRef.current = true;
              setActive(i);
            }}
            onBlur={() => {
              pausedRef.current = false;
            }}
            onMouseEnter={() => {
              pausedRef.current = true;
              setActive(i);
            }}
            onMouseLeave={() => {
              pausedRef.current = false;
            }}
            style={{
              padding: "20px 16px",
              borderLeft: i === 0 ? "none" : `1px dashed ${theme.border}`,
              position: "relative",
              cursor: "pointer",
              textAlign: "left",
              background: "transparent",
              font: "inherit",
              color: "inherit",
              borderTop: "none",
              borderRight: "none",
              borderBottom: "none",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontFamily: theme.mono,
                color: theme.fgMute,
                marginBottom: 10,
                letterSpacing: 0.8,
              }}
            >
              0{i + 1}
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: theme.fg,
                letterSpacing: -0.3,
                marginBottom: 12,
              }}
            >
              {s}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  height: 4,
                  background: theme.border,
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: theme.accent,
                    transform: i <= active ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: "left",
                    transition: "transform .6s cubic-bezier(.2,.7,.3,1)",
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontFamily: theme.mono,
                  color: i === active ? theme.accentFg : theme.fgMute,
                  minHeight: 14,
                }}
              >
                {i === active ? "● processing" : i < active ? "✓ complete" : "○ pending"}
              </div>
            </div>
          </button>
        ))}
        <div
          style={{
            position: "absolute",
            top: 58,
            left: `${(active + 0.5) * 20}%`,
            width: 10,
            height: 10,
            borderRadius: 6,
            background: theme.accent,
            transform: "translateX(-50%)",
            transition: "left .6s cubic-bezier(.2,.7,.3,1)",
            boxShadow: `0 0 0 4px ${theme.accentSoft}`,
          }}
        />
      </div>

      <div
        style={{
          marginTop: 28,
          paddingTop: 20,
          borderTop: `1px solid ${theme.border}`,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 24,
          fontSize: 13,
        }}
      >
        <MetricTile theme={theme} label="Stakeholders aligned" value="7 / 7" trend="up" />
        <MetricTile theme={theme} label="Avg. decision confidence" value="92%" trend="up" />
        <MetricTile theme={theme} label="Agency spend" value="−64%" trend="down" />
      </div>
    </div>
  );
}

function MetricTile({
  theme,
  label,
  value,
  trend,
}: {
  theme: Theme;
  label: string;
  value: string;
  trend: "up" | "down";
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          color: theme.fgMute,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: theme.fg,
          letterSpacing: -0.6,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {value}
        <span style={{ fontSize: 11, color: theme.accentFg, opacity: 0.7 }}>
          {trend === "up" ? "▲" : "▼"}
        </span>
      </div>
    </div>
  );
}
